import type {
  DealCommitmentAllocationHeaderV1,
  DealCaseV1,
  DealMeasurementBindingV1,
  PromiseBand,
  PromiseKind,
  PromiseSetV1,
  ValueBasis,
} from './ma-deal-case';
import {
  DATA_LAKE_SOURCE_CLASS_MAPPINGS,
  DECISION_AGENT_SOURCE_CLASSES,
  sourceClassPackagePosture,
  type DecisionAgentSourceClass,
} from './contracts/sourceClasses';
import {
  isFourSeriesEvidenceBasis,
} from './four-series-assessment';
import {
  validateDealThesisOutcomeV1,
  type DealThesisOutcomeV1,
} from './ma-deal-thesis';
import { maDealAllocationBreakdownsVerdict } from './ma-deal-allocation-breakdown.validation';

const PROMISE_BANDS = ['conservative', 'base', 'stretch'] as const;
const SERIES_NAMES = ['originalPromise', 'currentPlan', 'actual', 'forecast'] as const;
const VALUE_BASES: readonly ValueBasis[] = ['run_rate', 'in_period', 'cumulative', 'one_time'];
const PROMISE_KINDS: readonly PromiseKind[] = [
  'investment_committee',
  'board_approved',
  'modeled_underwriting_overlay',
  'publicly_communicated',
  'close_date_integration',
];

export const MA_DEAL_VALUE_BASIS_UNATTESTED = 'value_basis_unattested';
export type MaDealValueBasisGapState = 'absent' | 'unattested' | 'invalid';

/** The complete compiler/read admission-invariant surface. */
export const MA_DEAL_PACK_INVARIANT_CODES = [
  'pack_shape',
  'promise_set_governance',
  'promise_set_identity',
  'deal_case_relation',
  'commitment_economics',
  'measurement_binding_relation',
  'realization_shape',
  'pack_uniqueness',
  'thesis_relation',
  'allocation_breakdown',
] as const;

export type MaDealPackVerdictCode = typeof MA_DEAL_PACK_INVARIANT_CODES[number];

export type MaDealPackProblemVerdict =
  | Readonly<{ ok: true }>
  | Readonly<{ ok: false; code: MaDealPackVerdictCode; message: string }>;

export type MaDealPackAdmission = Readonly<{
  dealCase: DealCaseV1;
  promiseSets: readonly PromiseSetV1[];
  measurementBindings: readonly DealMeasurementBindingV1[];
  realization?: readonly unknown[];
  thesisOutcomes?: readonly DealThesisOutcomeV1[];
  allocationBreakdowns?: readonly DealCommitmentAllocationHeaderV1[];
}>;

export type MaDealPackAdmissionVerdict =
  | Readonly<{ ok: true; value: MaDealPackAdmission }>
  | Readonly<{ ok: false; code: MaDealPackVerdictCode; message: string }>;

const BLANK_GAP_DETAIL = /^[\s\u034F\u200B-\u200D\u2060-\u2064\u206A-\u206F\uFEFF]*$/;

export function canonicalDealSourceClass(value: unknown): DecisionAgentSourceClass | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  const canonical = Object.values(DECISION_AGENT_SOURCE_CLASSES)
    .find((sourceClass) => sourceClass.toLowerCase() === normalized);
  if (canonical) return canonical;
  const alias = Object.values(DATA_LAKE_SOURCE_CLASS_MAPPINGS)
    .find((mapping) => mapping.raw.toLowerCase() === normalized);
  return alias?.canonical ?? null;
}

export function maDealValueBasisGapState(value: unknown): MaDealValueBasisGapState {
  if (value === undefined || value === null) return 'absent';
  if (!isRecord(value)) return 'invalid';
  if (value.code !== MA_DEAL_VALUE_BASIS_UNATTESTED) return 'invalid';
  return typeof value.detail === 'string' && !BLANK_GAP_DETAIL.test(value.detail)
    ? 'unattested'
    : 'invalid';
}

/** Shared, non-throwing identity invariant for every promise-set admission path. */
export function maDealPromiseSetIdentityProblem(value: unknown): MaDealPackProblemVerdict {
  if (!isRecord(value) || !Array.isArray(value.commitments)) {
    return invalid('promise_set_identity', 'A promise set must carry a commitments array.');
  }
  if (!Array.isArray(value.commitmentRefs)) {
    return invalid('promise_set_identity', 'A promise set must carry a commitmentRefs array.');
  }
  const commitments = value.commitments;
  const commitmentRefs = value.commitmentRefs;
  if (!commitments.every((commitment) => isRecord(commitment) && isNonEmptyString(commitment.commitmentRef))) {
    return invalid('promise_set_identity', 'Every commitment must declare a string commitmentRef.');
  }
  if (!commitmentRefs.every(isNonEmptyString)) {
    return invalid('promise_set_identity', 'Every commitment reference must be a non-empty string.');
  }
  const refs = commitments.map((commitment) => String(commitment.commitmentRef));
  if (new Set(refs).size !== refs.length) {
    return invalid('promise_set_identity', 'A promise set cannot contain duplicate commitment identities.');
  }
  if (commitmentRefs.length !== refs.length || refs.some((ref) => !commitmentRefs.includes(ref))) {
    return invalid('promise_set_identity', 'Commitment references must match commitment content.');
  }
  return valid();
}

/** Shared governance and economic predicate used before both read and write admission. */
export function maDealPromiseSetGovernanceVerdict(value: unknown): MaDealPackProblemVerdict {
  if (!isRecord(value)) return invalid('promise_set_governance', 'Promise set is not an object.');
  if (value.schemaVersion !== 'deal_promise_set_v1') return invalid('promise_set_governance', `Promise set ${String(value.promiseSetId)} has the wrong schema.`);
  if (!isNonEmptyString(value.promiseSetId) || !isNonEmptyString(value.dealRef)) return invalid('promise_set_governance', 'Promise set identities must be non-empty strings.');
  if (value.governanceState !== 'approved') return invalid('promise_set_governance', `Promise set ${value.promiseSetId} is not approved.`);
  if (typeof value.version !== 'number' || !Number.isInteger(value.version) || value.version < 1) return invalid('promise_set_governance', 'A promise set version must be a positive integer.');
  if (!isNonEmptyString(value.approvedAt) || !isNonEmptyString(value.approvedBy)) return invalid('promise_set_governance', 'An approved promise set requires approval metadata.');
  if (!PROMISE_KINDS.includes(value.promiseKind as PromiseKind)) return invalid('promise_set_governance', `Promise set ${value.promiseSetId} has unsupported promiseKind.`);
  // #5841 terra HOLD r3 finding 4, ported into the shared surface during the fold. Production DB
  // rows are schema-non-null for both refs, but the file-pack path had no equivalent check, so a
  // malformed pack could admit a null or blank reportingCurrencyRef/fiscalCalendarRef -
  // self-consistent under the digest, since a digest proves a record is UNCHANGED, not
  // WELL-FORMED - and reach maDealArgumentExport as an export with no usable currency or period
  // ref. Main added these to the reader-local promiseSetProblem this change deletes; they belong
  // on the shared surface so BOTH gates enforce them, which is the whole point of #5905.
  if (!isNonEmptyString(value.reportingCurrencyRef)) return invalid('promise_set_governance', `Promise set ${String(value.promiseSetId)} is missing reportingCurrencyRef.`);
  if (!isNonEmptyString(value.fiscalCalendarRef)) return invalid('promise_set_governance', `Promise set ${String(value.promiseSetId)} is missing fiscalCalendarRef.`);
  if (!VALUE_BASES.includes(value.valueBasis as ValueBasis)) return invalid('promise_set_governance', `Promise set ${value.promiseSetId} declares a value basis outside the governed enum.`);
  const bands = maDealBandSemanticsVerdict(value.bandSemantics, `Promise set ${value.promiseSetId}`);
  if (!bands.ok) return bands;
  if (!Array.isArray(value.commitments) || value.commitments.length === 0) return invalid('promise_set_governance', `Promise set ${value.promiseSetId} has no commitments.`);
  for (const commitment of value.commitments) {
    const commitmentVerdict = maDealCommitmentGovernanceVerdict(commitment, value);
    if (!commitmentVerdict.ok) return commitmentVerdict;
  }
  return maDealPromiseSetIdentityProblem(value);
}

export function maDealPackAdmissionVerdict(input: unknown): MaDealPackAdmissionVerdict {
  if (!isRecord(input)) return invalid('pack_shape', 'Pack is not an object.');
  const shape = maDealPackShapeVerdict(input);
  if (!shape.ok) return shape;
  const promiseSets = input.promiseSets as readonly unknown[];
  for (const promiseSet of promiseSets) {
    const verdict = maDealPromiseSetGovernanceVerdict(promiseSet);
    if (!verdict.ok) return verdict;
  }
  const relations = maDealSuppliedDealCaseRelationsVerdict(input.dealCase, promiseSets);
  if (!relations.ok) return relations;
  const allocation = maDealAllocationBreakdownsVerdict(input.allocationBreakdowns, promiseSets);
  if (!allocation.ok) return invalid('allocation_breakdown', allocation.message);
  const bindings = maDealMeasurementBindingsVerdict(promiseSets, input.measurementBindings as readonly unknown[]);
  if (!bindings.ok) return bindings;
  const realization = maDealRealizationRecordsVerdict(input.realization, promiseSets);
  if (!realization.ok) return realization;
  const uniqueness = maDealSuppliedPackUniquenessVerdict(input);
  if (!uniqueness.ok) return uniqueness;
  return { ok: true, value: input as unknown as MaDealPackAdmission };
}

/**
 * The reader alone can receive separately produced thesis outcomes; the compiler cannot invent
 * them. Keep their relation separate so each caller validates only the scope it possesses.
 */
export function maDealWholePackAdmissionVerdict(input: unknown): MaDealPackAdmissionVerdict {
  const admission = maDealPackAdmissionVerdict(input);
  if (!admission.ok) return admission;
  const pack = input as Record<string, unknown>;
  const promiseSets = pack.promiseSets as readonly unknown[];
  const relations = maDealWholeDealCaseRelationsVerdict(pack.dealCase, promiseSets);
  if (!relations.ok) return relations;
  const outcomes = pack.thesisOutcomes;
  if (outcomes === undefined) return admission;
  const thesis = maDealThesisRecordsVerdict(pack.dealCase, outcomes as readonly unknown[], promiseSets);
  if (!thesis.ok) return thesis;
  const uniqueness = maDealWholePackUniquenessVerdict(outcomes as readonly unknown[]);
  if (!uniqueness.ok) return uniqueness;
  return admission;
}

/** Normalize accepted aliases once, at the declared raw-pack ingestion boundary. */
export function normalizeMaDealPackSourceClasses(pack: MaDealPackAdmission): MaDealPackAdmission {
  if (!pack.realization) return pack;
  return {
    ...pack,
    realization: pack.realization.map(normalizeRealizationRecord),
  } as MaDealPackAdmission;
}

function maDealPackShapeVerdict(pack: Record<string, unknown>): MaDealPackProblemVerdict {
  if (!isRecord(pack.dealCase) || pack.dealCase.schemaVersion !== 'deal_case_v1' || !isNonEmptyString(pack.dealCase.dealRef)) {
    return invalid('pack_shape', 'Pack dealCase is not a deal_case_v1 record.');
  }
  if (!Array.isArray(pack.promiseSets) || pack.promiseSets.length === 0) return invalid('pack_shape', 'Pack has no promise sets.');
  if (!Array.isArray(pack.measurementBindings)) return invalid('pack_shape', 'Pack measurementBindings must be an array.');
  if (pack.realization !== undefined && !Array.isArray(pack.realization)) return invalid('pack_shape', 'Pack realization must be an array when supplied.');
  if (pack.thesisOutcomes !== undefined && !Array.isArray(pack.thesisOutcomes)) return invalid('pack_shape', 'Pack thesisOutcomes must be an array when supplied.');
  if (pack.allocationBreakdowns !== undefined && !Array.isArray(pack.allocationBreakdowns)) return invalid('pack_shape', 'Pack allocationBreakdowns must be an array when supplied.');
  return valid();
}

function maDealBandSemanticsVerdict(value: unknown, prefix: string): MaDealPackProblemVerdict {
  if (!isRecord(value) || !Array.isArray(value.bands) || value.bands.length === 0) {
    return invalid('commitment_economics', `${prefix} must declare a non-empty bands array.`);
  }
  if (!value.bands.every((band) => PROMISE_BANDS.includes(band as PromiseBand))) {
    return invalid('commitment_economics', `${prefix} declares an unsupported promise band.`);
  }
  if (new Set(value.bands).size !== value.bands.length) {
    return invalid('commitment_economics', `${prefix} declares a duplicate promise band.`);
  }
  return valid();
}

function maDealCommitmentGovernanceVerdict(commitment: unknown, promiseSet: Record<string, unknown>): MaDealPackProblemVerdict {
  if (!isRecord(commitment)) return invalid('promise_set_governance', `Promise set ${String(promiseSet.promiseSetId)} contains a non-object commitment.`);
  if (commitment.schemaVersion !== 'deal_commitment_v1') return invalid('promise_set_governance', `Commitment ${String(commitment.commitmentRef)} has the wrong schema.`);
  if (!isNonEmptyString(commitment.displayLabel)) return invalid('promise_set_governance', `Commitment ${String(commitment.commitmentRef)} is missing displayLabel.`);
  if (commitment.dealRef !== promiseSet.dealRef) return invalid('deal_case_relation', 'Commitments must belong to the promise set deal.');
  if (commitment.promiseSetRef !== promiseSet.promiseSetId) return invalid('deal_case_relation', 'Commitments must belong to the promise set reference.');
  if (!Array.isArray(commitment.thesisOutcomeRefs)) return invalid('thesis_relation', `Commitment ${String(commitment.commitmentRef)} thesisOutcomeRefs must be an array when supplied.`);
  if (commitment.certaintyClass !== 'c1_committed_lever' && commitment.certaintyClass !== 'c2_modelled_upside') return invalid('promise_set_governance', `Commitment ${String(commitment.commitmentRef)} has unsupported certaintyClass.`);
  if (promiseSet.promiseKind === 'modeled_underwriting_overlay' && commitment.certaintyClass !== 'c2_modelled_upside') return invalid('promise_set_governance', `Commitment ${String(commitment.commitmentRef)} has unsupported certaintyClass for modeled_underwriting_overlay.`);
  return maDealCommitmentEconomicsVerdict(commitment, promiseSet);
}

function maDealCommitmentEconomicsVerdict(commitment: Record<string, unknown>, promiseSet: Record<string, unknown>): MaDealPackProblemVerdict {
  if (!isRecord(commitment.targetByBand)) return invalid('commitment_economics', `Commitment ${String(commitment.commitmentRef)} must declare governed target bands.`);
  for (const band of PROMISE_BANDS) {
    const target = commitment.targetByBand[band];
    if (!isRecord(target) || !Number.isFinite(target.value)) return invalid('commitment_economics', `Commitment ${String(commitment.commitmentRef)} targetByBand.${band}.value must be finite.`);
    if (target.valueBasis !== promiseSet.valueBasis) return invalid('commitment_economics', 'Every commitment band target must match the promise-set value basis.');
  }
  if (!isRecord(commitment.costToAchieve) || !Number.isFinite(commitment.costToAchieve.value)) return invalid('commitment_economics', `Commitment ${String(commitment.commitmentRef)} costToAchieve.value must be finite.`);
  const bands = maDealBandSemanticsVerdict(commitment.bandSemantics, `Commitment ${String(commitment.commitmentRef)}`);
  if (!bands.ok) return bands;
  if (commitment.measurementBindingRef !== null && !isNonEmptyString(commitment.measurementBindingRef)) return invalid('measurement_binding_relation', `Commitment ${String(commitment.commitmentRef)} measurementBindingRef must be a string or null.`);
  return valid();
}

function maDealSuppliedDealCaseRelationsVerdict(dealCase: unknown, promiseSets: readonly unknown[]): MaDealPackProblemVerdict {
  const caseRecord = dealCase as Record<string, unknown>;
  if (!Array.isArray(caseRecord.promiseSetRefs) || !(caseRecord.promiseSetRefs as readonly unknown[]).every(isNonEmptyString)) return invalid('deal_case_relation', 'dealCase.promiseSetRefs must be an array of non-empty references.');
  const promiseSetRefs = caseRecord.promiseSetRefs as readonly string[];
  const setRefs = promiseSets.map((set) => String((set as Record<string, unknown>).promiseSetId));
  if (setRefs.some((ref) => !promiseSetRefs.includes(ref))) {
    return invalid('deal_case_relation', 'dealCase.promiseSetRefs must include supplied promise sets.');
  }
  const caseDealRef = caseRecord.dealRef;
  if (promiseSets.some((set) => (set as Record<string, unknown>).dealRef !== caseDealRef)) return invalid('deal_case_relation', 'Promise sets must belong to the deal case.');
  return valid();
}

function maDealWholeDealCaseRelationsVerdict(dealCase: unknown, promiseSets: readonly unknown[]): MaDealPackProblemVerdict {
  const caseRecord = dealCase as Record<string, unknown>;
  const promiseSetRefs = caseRecord.promiseSetRefs as readonly string[];
  const setRefs = promiseSets.map((set) => String((set as Record<string, unknown>).promiseSetId));
  if (promiseSetRefs.length !== setRefs.length || promiseSetRefs.some((ref) => !setRefs.includes(ref))) {
    return invalid('deal_case_relation', 'dealCase.promiseSetRefs must match supplied promise sets.');
  }
  return valid();
}

function maDealMeasurementBindingsVerdict(promiseSets: readonly unknown[], bindings: readonly unknown[]): MaDealPackProblemVerdict {
  const commitments = promiseSets.flatMap((set) => (set as Record<string, unknown>).commitments as readonly Record<string, unknown>[]);
  const byCommitmentRef = new Map(commitments.map((commitment) => [String(commitment.commitmentRef), commitment]));
  const bindingRefs = new Set<string>();
  for (const binding of bindings) {
    if (!isRecord(binding) || binding.schemaVersion !== 'deal_measurement_binding_v1' || !isNonEmptyString(binding.measurementBindingRef) || !isNonEmptyString(binding.commitmentRef)) return invalid('measurement_binding_relation', 'Measurement bindings must declare governed binding and commitment references.');
    bindingRefs.add(binding.measurementBindingRef);
    const commitment = byCommitmentRef.get(binding.commitmentRef);
    if (!commitment) return invalid('measurement_binding_relation', `measurementBinding ${binding.measurementBindingRef} references unknown commitment.`);
    if (commitment.measurementBindingRef !== binding.measurementBindingRef) return invalid('measurement_binding_relation', `measurementBinding ${binding.measurementBindingRef} does not match commitment ${binding.commitmentRef} measurementBindingRef.`);
  }
  const missing = commitments.find((commitment) => commitment.measurementBindingRef !== null && !bindingRefs.has(String(commitment.measurementBindingRef)));
  return missing ? invalid('measurement_binding_relation', `Commitment ${String(missing.commitmentRef)} declares measurementBindingRef without supplied binding.`) : valid();
}

function maDealRealizationRecordsVerdict(records: unknown, promiseSets: readonly unknown[]): MaDealPackProblemVerdict {
  if (records === undefined) return valid();
  const byRef = new Map<string, Readonly<{ commitment: Record<string, unknown>; valueBasis: unknown }>>();
  for (const promiseSet of promiseSets) {
    const set = promiseSet as Record<string, unknown>;
    for (const commitment of set.commitments as readonly Record<string, unknown>[]) {
      byRef.set(String(commitment.commitmentRef), { commitment, valueBasis: set.valueBasis });
    }
  }
  for (const [index, record] of (records as readonly unknown[]).entries()) {
    const verdict = maDealRealizationRecordVerdict(record, index, byRef);
    if (!verdict.ok) return verdict;
  }
  return valid();
}

function maDealRealizationRecordVerdict(record: unknown, index: number, commitments: ReadonlyMap<string, Readonly<{ commitment: Record<string, unknown>; valueBasis: unknown }>>): MaDealPackProblemVerdict {
  const prefix = `Pack realization[${index}]`;
  if (!isRecord(record)) return invalid('realization_shape', `${prefix} is not an object.`);
  const commitment = commitments.get(String(record.commitmentRef));
  if (!commitment) return invalid('realization_shape', `${prefix} references an unknown commitment.`);
  const basis = maDealComparisonBasisVerdict(record, commitment.valueBasis, prefix);
  if (!basis.ok) return basis;
  if (!isRecord(record.forecastGap) || record.forecastGap.code !== 'forecast_not_supplied') return invalid('realization_shape', `${prefix} must declare forecast_not_supplied.`);
  if (!isRecord(record.series) || Object.keys(record.series).length !== SERIES_NAMES.length || SERIES_NAMES.some((name) => !Object.prototype.hasOwnProperty.call(record.series, name))) return invalid('realization_shape', `${prefix} must contain exactly the four governed series.`);
  for (const seriesName of SERIES_NAMES) {
    const points = record.series[seriesName];
    const pointVerdict = maDealSeriesPointsVerdict(points, seriesName, prefix);
    if (!pointVerdict.ok) return pointVerdict;
  }
  return valid();
}

function maDealComparisonBasisVerdict(record: Record<string, unknown>, expectedValueBasis: unknown, prefix: string): MaDealPackProblemVerdict {
  const basis = record.comparisonBasis;
  if (!isRecord(basis) || !isNonEmptyString(basis.metricKey) || !isNonEmptyString(basis.unit)) return invalid('realization_shape', `${prefix} comparisonBasis is not comparable to its promise set.`);
  const gapState = maDealValueBasisGapState(record.valueBasisGap);
  if (gapState === 'invalid') return invalid('realization_shape', `${prefix} valueBasisGap must declare ${MA_DEAL_VALUE_BASIS_UNATTESTED} with a detail.`);
  if (gapState === 'unattested') return basis.valueBasis === null ? valid() : invalid('realization_shape', `${prefix} comparisonBasis valueBasis must be null when ${MA_DEAL_VALUE_BASIS_UNATTESTED}.`);
  if (!VALUE_BASES.includes(basis.valueBasis as ValueBasis)) return invalid('realization_shape', `${prefix} comparisonBasis is not comparable to its promise set.`);
  return basis.valueBasis === expectedValueBasis
    ? valid()
    : invalid('realization_shape', `${prefix} comparisonBasis is not comparable to its promise set.`);
}

function maDealSeriesPointsVerdict(points: unknown, seriesName: typeof SERIES_NAMES[number], prefix: string): MaDealPackProblemVerdict {
  if (!Array.isArray(points)) return invalid('realization_shape', `${prefix}.${seriesName} must be an array.`);
  if (seriesName === 'forecast' && points.length > 0) return invalid('realization_shape', `${prefix}.forecast must be empty when forecast_not_supplied.`);
  for (const point of points) {
    if (!isRecord(point) || !validSeriesDate(point.asOf) || !Number.isFinite(point.value) || !isFourSeriesEvidenceBasis(point.evidenceBasis)) return invalid('realization_shape', `${prefix}.${seriesName} contains an invalid point.`);
    const sourceClass = canonicalDealSourceClass(point.sourceClass);
    if (seriesName === 'actual') {
      if (point.evidenceBasis !== 'observed' && point.evidenceBasis !== 'attested' && point.evidenceBasis !== 'modeled') return invalid('realization_shape', `${prefix}.actual must be observed, attested, or modeled.`);
      // An ungoverned (null) sourceClass is legacy-compatible only for attested actuals —
      // admitted packs recorded before governed sourceClass was required on actuals (e.g.
      // apps/api/src/decision-modules/ma-deal/__fixtures__/active-tenant.pack.json) still
      // carry sourceClass: null here. A modeled or observed actual must always declare a
      // governed sourceClass, and a declared-but-ungoverned sourceClass is always rejected
      // below.
      if (point.sourceClass === null) {
        if (point.evidenceBasis !== 'attested') return invalid('realization_shape', `${prefix}.actual sourceClass is not governed.`);
      } else {
        if (sourceClass === null) return invalid('realization_shape', `${prefix}.actual sourceClass is not governed.`);
        const sourcePosture = sourceClassPackagePosture(sourceClass);
        const sourceAgreesWithBasis =
          ((point.evidenceBasis === 'observed' || point.evidenceBasis === 'attested') && sourcePosture === 'source_receipted') ||
          (point.evidenceBasis === 'modeled' && sourceClass === DECISION_AGENT_SOURCE_CLASSES.modeled);
        if (!sourceAgreesWithBasis) return invalid('realization_shape', `${prefix}.actual evidenceBasis and sourceClass must agree.`);
      }
    } else if (point.sourceClass !== null && sourceClass === null) {
      return invalid('realization_shape', `${prefix}.${seriesName} sourceClass is not governed.`);
    }
    if (!validMilestoneState(point.milestoneState)) return invalid('realization_shape', `${prefix}.${seriesName} milestoneState is invalid.`);
  }
  return valid();
}

function maDealThesisRecordsVerdict(dealCase: unknown, outcomes: readonly unknown[], promiseSets: readonly unknown[]): MaDealPackProblemVerdict {
  const caseRecord = dealCase as Record<string, unknown>;
  if (!Array.isArray(caseRecord.thesisOutcomeRefs) || !(caseRecord.thesisOutcomeRefs as readonly unknown[]).every(isNonEmptyString)) return invalid('thesis_relation', 'dealCase.thesisOutcomeRefs must be an array of non-empty references.');
  const thesisOutcomeRefs = caseRecord.thesisOutcomeRefs as readonly string[];
  const outcomeRows = outcomes;
  const outcomeIds = new Set<string>();
  const promiseSetIds = new Set(promiseSets.map((set) => String((set as Record<string, unknown>).promiseSetId)));
  for (const [index, outcome] of outcomeRows.entries()) {
    const validation = validateDealThesisOutcomeV1(outcome);
    if (!validation.ok) return invalid('thesis_relation', `Pack thesisOutcomes[${index}] is invalid: ${validation.errors.join('; ')}`);
    const foreignExposureRef = validation.value.orgExposureBindings?.some((binding) =>
      [binding.sourceRef, binding.targetRef, ...binding.sourceRefs].some((ref) =>
        ref.tenantRef !== undefined && ref.tenantRef !== caseRecord.tenantRef,
      ),
    );
    if (foreignExposureRef) return invalid('thesis_relation', `Pack thesisOutcomes[${index}] organization binding references another tenant.`);
    const outcomeId = governedRefId(validation.value.thesisOutcomeRef);
    if (!outcomeId || governedRefId(validation.value.dealRef) !== caseRecord.dealRef) return invalid('thesis_relation', `Pack thesisOutcomes[${index}] references another deal.`);
    if (!promiseSetIds.has(String(governedRefId(validation.value.promiseSetRef)))) return invalid('thesis_relation', `Pack thesisOutcomes[${index}] references an unknown promise set.`);
    outcomeIds.add(outcomeId);
  }
  if (thesisOutcomeRefs.some((ref) => !outcomeIds.has(ref)) || [...outcomeIds].some((ref) => !thesisOutcomeRefs.includes(ref))) return invalid('thesis_relation', 'dealCase.thesisOutcomeRefs must match supplied thesis outcomes.');
  const commitmentRefs = promiseSets.flatMap((set) => (set as Record<string, unknown>).commitments as readonly Record<string, unknown>[]);
  const orphan = commitmentRefs.flatMap((commitment) => commitment.thesisOutcomeRefs as readonly unknown[]).find((ref) => !isNonEmptyString(ref) || !outcomeIds.has(ref));
  return orphan === undefined ? valid() : invalid('thesis_relation', `Commitment thesisOutcomeRefs references unknown thesis outcome ${String(orphan)}.`);
}

/** One supplied-scope identity pass; a complete reader pack can extend it with thesis records. */
function maDealSuppliedPackUniquenessVerdict(pack: Record<string, unknown>): MaDealPackProblemVerdict {
  const promiseSets = pack.promiseSets as readonly Record<string, unknown>[];
  const commitments = promiseSets.flatMap((set) => set.commitments as readonly Record<string, unknown>[]);
  const bindingRows = pack.measurementBindings as readonly Record<string, unknown>[];
  const realization = (pack.realization ?? []) as readonly Record<string, unknown>[];
  const checks: readonly [readonly Record<string, unknown>[], string, (row: Record<string, unknown>) => string | null][] = [
    [promiseSets, 'promiseSetId', (row) => stringId(row.promiseSetId)],
    [commitments, 'commitmentRef', (row) => stringId(row.commitmentRef)],
    [commitments, 'commitment measurementBindingRef', (row) => stringId(row.measurementBindingRef)],
    [bindingRows, 'measurementBindingRef', (row) => stringId(row.measurementBindingRef)],
    [bindingRows, 'measurement binding commitmentRef', (row) => stringId(row.commitmentRef)],
    [realization, 'realization commitmentRef', (row) => stringId(row.commitmentRef)],
  ];
  for (const [rows, label, readId] of checks) {
    const duplicate = firstDuplicate(rows, readId);
    if (duplicate) return invalid('pack_uniqueness', `Pack contains duplicate ${label} id: ${duplicate}.`);
  }
  return valid();
}

function maDealWholePackUniquenessVerdict(outcomes: readonly unknown[]): MaDealPackProblemVerdict {
  const rows = outcomes.filter(isRecord);
  const checks: readonly [readonly Record<string, unknown>[], string, (row: Record<string, unknown>) => string | null][] = [
    [rows, 'thesisOutcomeRef', (row) => governedRefId(row.thesisOutcomeRef)],
    [rows.flatMap((outcome) => (outcome.disconfirmingConditions ?? []) as Record<string, unknown>[]), 'disconfirming conditionRef', (row) => governedRefId(row.conditionRef)],
  ];
  for (const [values, label, readId] of checks) {
    const duplicate = firstDuplicate(values, readId);
    if (duplicate) return invalid('pack_uniqueness', `Pack contains duplicate ${label} id: ${duplicate}.`);
  }
  return valid();
}

function normalizeRealizationRecord(record: unknown): unknown {
  if (!isRecord(record) || !isRecord(record.series)) return record;
  const series = Object.fromEntries(Object.entries(record.series).map(([name, points]) => [
    name,
    Array.isArray(points) ? points.map(normalizeRealizationPoint) : points,
  ]));
  return { ...record, series };
}

function normalizeRealizationPoint(point: unknown): unknown {
  if (!isRecord(point) || point.sourceClass === null) return point;
  const sourceClass = canonicalDealSourceClass(point.sourceClass);
  return sourceClass ? { ...point, sourceClass } : point;
}

function validMilestoneState(value: unknown): boolean {
  return value === null || (isRecord(value) && (value.status === 'on_track' || value.status === 'slipped') && validSeriesDate(value.asOf));
}

function validSeriesDate(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?Z)?$/.exec(value);
  if (!match) return false;
  const timestamp = Date.parse(value.length === 10 ? `${value}T00:00:00.000Z` : value);
  if (!Number.isFinite(timestamp)) return false;
  const date = new Date(timestamp);
  return Number(match[1]) === date.getUTCFullYear() && Number(match[2]) - 1 === date.getUTCMonth() && Number(match[3]) === date.getUTCDate();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function stringId(value: unknown): string | null {
  return isNonEmptyString(value) ? value : null;
}

function governedRefId(value: unknown): string | null {
  return isRecord(value) ? stringId(value.id) : null;
}

function firstDuplicate<T>(values: readonly T[], readId: (value: T) => string | null): string | null {
  const seen = new Set<string>();
  for (const value of values) {
    const id = readId(value);
    if (id && seen.has(id)) return id;
    if (id) seen.add(id);
  }
  return null;
}

function valid(): MaDealPackProblemVerdict {
  return { ok: true };
}

function invalid(code: MaDealPackVerdictCode, message: string): Extract<MaDealPackProblemVerdict, { ok: false }> {
  return { ok: false, code, message };
}
